import React, { useEffect, useState } from 'react';
import SectionHomeWrapper from '../common/wrappers/SectionHomeWrapper';
import OnPremPORequestObjectService from '../../services/OnPremObjectRequestService';
import { AlertBanner } from '../../models/interfaces/AlertBanner';
import AlertModal from '../common/modals/AlertModal';
import Paginator from '../common/tables/Paginator';
import './OnPremPortOpeningRequestsHome.css';
import { PortOpeningRequestsDirection } from '../../models/enums/POR/PortOpeningRequestsDirection';
import { CheckboxOption } from '../common/tables/CheckboxFilter';
import Filter from '../common/tables/Filter';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Memoized from '../common/Memoized';
import { OnPremPortOpeningRequestsTypes } from '../../models/enums/POR/OnPremPortOpeningRequestsTypes';
import '../common/CommonElements.css';
import OnPremPORequestObjectOptimized from '../../models/entities/OnPremPORequestObjectOptimized';
import { AlertVariantType, ProgressBar } from '../common';
import { ButtonGroup } from '../common/components/ButtonGroup';
import Table from '../common/components/Table';
import OnPremPORequestObjectsHomeTableRow from './OnPremPORequestObjectsHomeTableRow';
import WorkflowStateBadge from '../common/WorkflowStateBadge';
import moment from 'moment';
import PageResult from '../../models/entities/PageResult';
import OnPremPORequestObjectsHomeFilters from './OnPremPORequestObjectsHomeFilters';
import { TextLink } from '../common/components/TextLinkNew';

const StyledCheckbox = styled.input`
	margin: 0 auto !important;
`;

interface OnPremPORequestObjectsHomeProps {
	subtitle?: string;
}

export type OnPremObjectsMemoizedData = {
	searchUser: void;
	status: void;
	scope: void;
	type: void;
	orderBy: void;
	fromDate: void;
	toDate: void;
};

const defaultMemoizedDataStructure = {
	searchUser: undefined,
	orderBy: undefined,
	scope: undefined,
	type: undefined,
	status: undefined,
	fromDate: undefined,
	toDate: undefined,
} as OnPremObjectsMemoizedData;

const memoized = new Memoized(defaultMemoizedDataStructure);

const OnPremPORequestObjectsHome = ({ subtitle }: OnPremPORequestObjectsHomeProps) => {
	const onPremPORequestObjectService = new OnPremPORequestObjectService();
	const navigate = useNavigate();
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingTable, setLoadingTable] = useState<boolean>(false);
	const [requests, setRequests] = useState<OnPremPORequestObjectOptimized[]>([]);
	const [filteredRequests, setFilteredRequests] = useState<OnPremPORequestObjectOptimized[]>([]);
	const [visibleRequests, setVisibleRequests] = useState<OnPremPORequestObjectOptimized[]>([]);
	const [page, setPage] = useState<number>(1);
	const [pageResult, setPageResult] = useState<PageResult>(null);
	const [endModal, setEndModal] = useState<AlertBanner>({ visible: false, text: '', variant: 'success' });

	useEffect(() => {
		loadRequests();
	}, []);

	const loadRequests = async () => {
		try {
			setLoading(true);
			const response = await onPremPORequestObjectService.getAll(page.toString());
			const requests = response?.data.map((r) => new OnPremPORequestObjectOptimized(r)).reverse();
			setRequests(requests);
			setPageResult(response);
			setLoading(false);
		} catch (ex) {
			console.log(ex);
			setLoading(false);
			handleEndModal('Something went wrong, try again later.', 'error', 5000);
		}
	};

	const handleNewPage = async () => {
		setLoadingTable(true);
		const response = await onPremPORequestObjectService.getAll((page + 1).toString());
		const newRequests = response?.data.map((r) => new OnPremPORequestObjectOptimized(r)).reverse();
		setRequests((prev) => [...prev, ...newRequests]);
		setPageResult(response);
		setPage(page + 1);
		setLoadingTable(false);
	};

	const handleEndModal = (text: string, variant: AlertVariantType, timeout: number) => {
		setEndModal({
			visible: true,
			text,
			variant,
		});
		setTimeout(() => {
			redirectToSubscriptions();
		}, timeout);
	};

	const redirectToSubscriptions = () => {
		setEndModal({
			visible: false,
		});
		navigate('/');
	};

	const endAndRedirect = () => {
		setEndModal({ visible: false });
		navigate('/');
	};

	const goToPORDetails = (request) => {
		navigate(`/onPremportOpeningObjects/${request.id}`);
	};

	const rows = visibleRequests?.map((request) => (
		<Table.Row key={request.id} onClick={() => goToPORDetails(request)}>
			<>{request.id}</>
			<>{request.type?.displayName ?? '—'}</>
			<>{request.direction?.displayName ?? '—'}</>
			<>{request.onPremWorkflowInstance?.createdBy?.displayName ?? '—'}</>
			<>{request.approverUser?.displayName ?? request.appEndorser?.displayName}</>
			<>{moment(request.onPremWorkflowInstance?.createdDate).format('YYYY-MM-DD')}</>
			<>
				<WorkflowStateBadge
					workflowState={request.onPremWorkflowInstance?.currentWorkflowInstanceState?.onPremWorkflowState}
					fullWidth
				/>
			</>
		</Table.Row>
	));

	return (
		<>
			<SectionHomeWrapper title={`Objects Requests ${subtitle ? `: ${subtitle}` : ''}`}>
				<p>
					For more information about Object Requests{' '}
					<TextLink className="em-u-margin-right-none" external target="_blank" href="https://goto/por">
						Click here
					</TextLink>
				</p>
				{loading ? (
					<ProgressBar label="Loading requests..." className="em-u-margin-top-half" indeterminate />
				) : (
					<>
						<OnPremPORequestObjectsHomeFilters
							setFilteredRequests={setFilteredRequests}
							requests={requests}
							loading={loading}
						/>
						<Table>
							<Table.Head>
								<Table.Head.Row>
									<Table.Head.Cell>Object #</Table.Head.Cell>
									<Table.Head.Cell>Type</Table.Head.Cell>
									<Table.Head.Cell>Scope</Table.Head.Cell>
									<Table.Head.Cell>Requestor</Table.Head.Cell>
									<Table.Head.Cell>Approver</Table.Head.Cell>
									<Table.Head.Cell>Requested Date</Table.Head.Cell>
									<Table.Head.Cell>Status</Table.Head.Cell>
								</Table.Head.Row>
							</Table.Head>
							<Table.Body>
								{!loadingTable ? (
									// visibleRequests?.map((req) => (
									// 	<OnPremPORequestObjectsHomeTableRow
									// 		key={req.id}
									// 		request={req}
									// 		goToPORDetails={goToPORDetails}
									// 	/>
									// ))
									rows
								) : (
									<Table.Row>
										<>Loading more requests...</>
									</Table.Row>
								)}
							</Table.Body>
						</Table>
						<Paginator
							data={filteredRequests}
							onPageChange={(page) => setVisibleRequests(page)}
							pageResult={pageResult}
							controller={onPremPORequestObjectService}
							handleNewPage={handleNewPage}
						/>
					</>
				)}
			</SectionHomeWrapper>
			<AlertModal {...endModal} willTimeout={false} onClose={endAndRedirect} />
		</>
	);
};

export default OnPremPORequestObjectsHome;
